import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Public as PublicIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  List as ListIcon,
  Map as MapIcon,
  FolderOpen as FolderOpenIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { styled } from '@mui/material/styles';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  Timestamp,
  updateDoc,
  deleteDoc,
  doc,
  type DocumentData,
  type UpdateData,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Report,
  User,
  Site,
  canUserViewReport,
  canUserEditReport,
  convertTimestamp,
  COLLECTIONS,
} from '../../../types/firebase';
import {
  approximateCoordinatesForPublicViewer,
  canUserDownloadReport,
  getPublicApproximateLocationLabel,
  getReportLocationAddressDisplay,
  getReportSiteNameDisplay,
  getReportSiteFolderLabel,
  getScannerDisplayLabel,
  getScannerRoleDisplay,
  includePublicReportInSharedViews,
  shouldRedactScannerIdentity,
} from '../../../utils/reportPrivacyDisplay';
import { generateEnhancedPDF } from '../../../services/enhancedPdfGenerator';
import { APPROX_AREA_CIRCLE_PATH } from '../../../utils/mapApproxAreaStyle';
import { isLawEnforcementPlan } from '../../../utils/subscriptionCategory';
import ReportDetailsDialog from '../ReportDetailsDialog';

// Import heat layer plugin
require('leaflet.heat');


const ReportCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.03)',
  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderTop: '1px solid rgba(255, 255, 255, 0.15)',
  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  marginBottom: '16px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(34, 211, 238, 0.3)',
    transform: 'translateY(-5px)',
    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
  },
  '&:hover::before': {
    left: '100%',
  },
}));

const isSkimmerDetectedStatus = (s: Report['status']) =>
  s === 'Skimmer Detected!' || s === 'Skimmer Detected';
const isNoSkimmerStatus = (s: Report['status']) => s === 'No Skimmer Detected';

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'reportStatus',
})<{ reportStatus: Report['status'] }>(({ reportStatus }) => {
  const isAlert = isSkimmerDetectedStatus(reportStatus);
  const isHealthy = isNoSkimmerStatus(reportStatus);
  const useCyan = isHealthy;
  const useRed = isAlert;
  return {
    color: useRed ? '#ff3b3b' : useCyan ? '#22d3ee' : '#ffffff',
    border: useRed
      ? '1px solid rgba(255, 59, 59, 0.3)'
      : useCyan
        ? '1px solid rgba(34, 211, 238, 0.3)'
        : '1px solid rgba(255, 255, 255, 0.15)',
    background: useRed
      ? 'rgba(255, 59, 59, 0.1)'
      : useCyan
        ? 'rgba(34, 211, 238, 0.1)'
        : 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    boxShadow: useRed
      ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 0 12px rgba(255, 59, 59, 0.15)'
      : useCyan
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 0 12px rgba(34, 211, 238, 0.15)'
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    borderRadius: '20px',
    fontWeight: 'bold',
    padding: '6px 14px',
    '&:hover': {
      transform: 'translateY(-2px)',
      background: useRed
        ? 'rgba(255, 59, 59, 0.2)'
        : useCyan
          ? 'rgba(34, 211, 238, 0.2)'
          : 'rgba(255, 255, 255, 0.2)',
    },
    '& .MuiChip-icon': {
      color: useRed ? '#ff3b3b' : useCyan ? '#22d3ee' : '#ffffff',
    },
  };
});

const PrivacyChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'privacy',
})<{ privacy: 'public' | 'private' }>(() => ({
  color: '#ffffff',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  background: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  borderRadius: '20px',
  fontWeight: 'bold',
  '&:hover': {
    transform: 'translateY(-2px)',
    background: 'rgba(255, 255, 255, 0.2)',
  },
  '& .MuiChip-icon': {
    color: '#ffffff',
  },
}));

interface ReportListRowCardProps {
  report: Report;
  userProfile: any;
  colors: any;
  downloadingReport: string | null;
  formatDate: (d: Date | Timestamp) => string;
  onOpenDetails: (r: Report) => void;
  onEdit: (r: Report, e: React.MouseEvent) => void;
  onDelete: (r: Report, e: React.MouseEvent) => void;
  onDownload: (r: Report, e: React.MouseEvent) => void;
}

/** Single report row — reused for flat list and folder drill-down */
const ReportListRowCard: React.FC<ReportListRowCardProps> = ({
  report,
  userProfile,
  colors,
  downloadingReport,
  formatDate,
  onOpenDetails,
  onEdit,
  onDelete,
  onDownload,
}) => {
  const dev = report.readerDetails?.device;
  return (
    <ReportCard onClick={() => onOpenDetails(report)} sx={{ cursor: 'pointer' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
              <Typography variant="h6" color={colors.TextPrimary} sx={{ fontWeight: 'bold' }}>
                {getReportSiteFolderLabel(report, userProfile?.masterOwnerID)}
              </Typography>
              <PrivacyChip
                label={report.privacy}
                privacy={report.privacy}
                size="small"
                icon={report.privacy === 'public' ? <PublicIcon /> : <PeopleIcon />}
              />
            </Box>
            <Typography variant="body2" color={colors.TextSecondary} gutterBottom>
              {getReportLocationAddressDisplay(report, userProfile?.masterOwnerID)}
            </Typography>
            <Typography variant="body2" color={colors.TextMuted}>
              {formatDate(report.createdAt)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <StatusChip label={report.status} reportStatus={report.status} size="small" />
          </Box>
        </Box>

        <Box marginBottom={2}>
          <Typography variant="subtitle2" color={colors.TextPrimary} gutterBottom>
            Device Information
          </Typography>
          <Typography variant="body2" color={colors.TextSecondary}>
            <strong>Type:</strong> {dev?.deviceType ?? '—'}
          </Typography>
          <Typography variant="body2" color={colors.TextSecondary}>
            <strong>Model:</strong> {dev?.modelName ?? '—'}
            {dev?.modelNumber != null && dev?.modelNumber !== '' ? ` (${dev.modelNumber})` : ''}
          </Typography>
          {dev?.specificLocation && !shouldRedactScannerIdentity(report, userProfile?.masterOwnerID) && (
            <Typography variant="body2" color={colors.TextSecondary}>
              <strong>Location:</strong> {dev.specificLocation}
            </Typography>
          )}
        </Box>

        <Box marginBottom={2}>
          <Typography variant="subtitle2" color={colors.TextPrimary} gutterBottom>
            Scan Details
          </Typography>
          <Typography variant="body2" color={colors.TextSecondary}>
            <strong>Scanned by:</strong> {getScannerRoleDisplay(report, userProfile?.masterOwnerID)}
          </Typography>
          <Typography variant="body2" color={colors.TextSecondary}>
            <strong>Result:</strong> {report.status}
          </Typography>
          {report.message && (
            <Typography variant="body2" color={colors.TextSecondary}>
              <strong>Notes:</strong> {report.message}
            </Typography>
          )}
        </Box>

        {report.location?.assignedUsers &&
          report.location.assignedUsers.length > 0 &&
          !shouldRedactScannerIdentity(report, userProfile?.masterOwnerID) && (
            <Box marginBottom={2}>
              <Typography variant="subtitle2" color={colors.TextPrimary} gutterBottom>
                Assigned Users
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {report.location.assignedUsers.map((user, index) => (
                  <Chip
                    key={index}
                    label={`${user.name} (${user.role})`}
                    size="small"
                    sx={{
                      backgroundColor: colors.Info,
                      color: colors.Light_Shadow,
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} marginTop={2} flexWrap="wrap">
          <Box display="flex" gap={1}>
            {userProfile && canUserEditReport(report, userProfile as User) && (
              <>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  size="small"
                  onClick={(e) => onEdit(report, e)}
                  sx={{
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                    },
                  }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<DeleteIcon />}
                  variant="outlined"
                  size="small"
                  onClick={(e) => onDelete(report, e)}
                  sx={{
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                    },
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
          {canUserDownloadReport(report, userProfile?.masterOwnerID) ? (
            <Button
              startIcon={downloadingReport === report.id ? <CircularProgress size={14} /> : <DownloadIcon />}
              variant="outlined"
              size="small"
              disabled={downloadingReport === report.id}
              onClick={(e) => onDownload(report, e)}
              sx={{
                color: '#ffffff',
                border: '1px solid rgba(34, 211, 238, 0.25)',
                background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                boxShadow:
                  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 12px rgba(34, 211, 238, 0.1), inset 0 1px 0 rgba(34, 211, 238, 0.08)',
                transition: 'all 0.3s ease',
                borderRadius: '50px',
                fontWeight: 'bold',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.12) 0%, rgba(255, 255, 255, 0.16) 100%)',
                  borderColor: 'rgba(34, 211, 238, 0.5)',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 0 16px rgba(34, 211, 238, 0.25), inset 0 1px 0 rgba(34, 211, 238, 0.12)',
                },
                '&:disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: colors.TextMuted,
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              {downloadingReport === report.id ? 'Generating...' : 'Download'}
            </Button>
          ) : (
            <Tooltip title="Only reports from your own company can be downloaded">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled
                  sx={{
                    color: colors.TextMuted,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                  }}
                >
                  Download
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </ReportCard>
  );
};

// Fix Leaflet default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DARK_TILES = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
/** Leaflet Circle radius is in meters; ~1 statute mile. */
const ONE_MILE_METERS = 1609.34;

const ReportsTab: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  /** Default to org (private) scans; use filter dialog for Public / all. */
  const [privacyFilter, setPrivacyFilter] = useState('private');
  const [radiusFilter, setRadiusFilter] = useState('15');
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
  const [companyAddress, setCompanyAddress] = useState<string | null>(null);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [mapDisplayMode, setMapDisplayMode] = useState<'markers' | 'heatmap'>('markers');
  const [analyticsTab, setAnalyticsTab] = useState<'overview' | 'patterns' | 'hotzones'>('overview');
  /** Mobile parity: Manual entry vs device scan */
  const [reportTypeFilter, setReportTypeFilter] = useState<'all' | 'manual' | 'device'>('all');
  /** Folder-style grouping in list view */
  const [listGroupMode, setListGroupMode] = useState<'none' | 'site' | 'month'>('site');
  /** null = folder summary cards; set to group key to show reports in that folder */
  const [folderDrillKey, setFolderDrillKey] = useState<string | null>(null);
  const [downloadingReport, setDownloadingReport] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedReportForAction, setSelectedReportForAction] = useState<Report | null>(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();

  const isGovPlan = useMemo(
    () => isLawEnforcementPlan(userProfile?.subscriptionPlan),
    [userProfile?.subscriptionPlan]
  );

  // Debug: Log user profile
  console.log('Current userProfile:', userProfile);
  console.log('User role:', userProfile?.role);
  console.log('User can edit reports based on new permission logic');

  useEffect(() => {
    applyFilters();
  }, [
    reports,
    statusFilter,
    privacyFilter,
    radiusFilter,
    siteFilter,
    userLocation,
    reportTypeFilter,
    viewMode,
    isGovPlan,
  ]);

  /** Government / sweeps: map is not scoped by corporate site; default to all distances. */
  useEffect(() => {
    if (isGovPlan) {
      setRadiusFilter('all');
    }
  }, [isGovPlan]);

  // Geocode company address to get coordinates for radius filtering
  const geocodeAddress = async (address: string): Promise<{ lat: number, lng: number } | null> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Get company address and geocode it for radius filtering
  useEffect(() => {
    const fetchCompanyAddress = async () => {
      if (!userProfile?.masterOwnerID) return;

      try {
        const companyRef = doc(db, COLLECTIONS.COMPANIES, userProfile.masterOwnerID);
        const companySnap = await getDoc(companyRef);

        if (companySnap.exists()) {
          const companyData = companySnap.data();
          const address = companyData.streetAddress1 || companyData.address;

          if (address) {
            setCompanyAddress(address);
            const coords = await geocodeAddress(address);

            if (coords) {
              setUserLocation(coords);
              setGeocodingError(null);
            } else {
              setGeocodingError('Could not locate company address');
              // Set a default location (center of US) if geocoding fails
              setUserLocation({ lat: 39.8283, lng: -98.5795 });
            }
          } else {
            setGeocodingError('No company address found in profile');
            // Set a default location if no address is found
            setUserLocation({ lat: 39.8283, lng: -98.5795 });
          }
        } else {
          setGeocodingError('Company profile not found');
          setUserLocation({ lat: 39.8283, lng: -98.5795 });
        }
      } catch (error) {
        console.error('Error fetching company address:', error);
        setGeocodingError('Failed to load company address');
        setUserLocation({ lat: 39.8283, lng: -98.5795 });
      }
    };

    fetchCompanyAddress();
  }, [userProfile]);

  const fetchReports = async () => {
    if (!userProfile) return;

    try {
      setLoading(true);
      const reportsRef = collection(db, COLLECTIONS.REPORTS);

      let reportsData: Report[] = [];

      if (userProfile.role === 'Clerk') {
        // Clerk users only see reports from same masterOwnerID and ownerID
        const privateQuery = query(
          reportsRef,
          where('user.masterOwnerID', '==', userProfile.masterOwnerID),
          where('user.ownerID', '==', userProfile.ownerID),
          orderBy('createdAt', 'desc')
        );
        const privateSnapshot = await getDocs(privateQuery);
        reportsData = privateSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: convertTimestamp(doc.data().createdAt),
        })) as Report[];
      } else {
        // Admin and Tech users see public reports and private reports from their masterOwnerID

        // Fetch public reports
        const publicQuery = query(
          reportsRef,
          where('privacy', '==', 'public'),
          orderBy('createdAt', 'desc')
        );
        const publicSnapshot = await getDocs(publicQuery);
        const publicReports = publicSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: convertTimestamp(doc.data().createdAt),
        })) as Report[];

        // Fetch private reports from same masterOwnerID
        const privateQuery = query(
          reportsRef,
          where('privacy', '==', 'private'),
          where('user.masterOwnerID', '==', userProfile.masterOwnerID),
          orderBy('createdAt', 'desc')
        );
        const privateSnapshot = await getDocs(privateQuery);
        const privateReports = privateSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: convertTimestamp(doc.data().createdAt),
        })) as Report[];

        // Combine and sort by date
        reportsData = [...publicReports, ...privateReports]
          .sort((a, b) => convertTimestamp(b.createdAt).getTime() - convertTimestamp(a.createdAt).getTime());
      }

      const visibleReports = reportsData
        .filter((report) => canUserViewReport(report, userProfile as User))
        .filter(includePublicReportInSharedViews);

      setReports(visibleReports);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      setError('Failed to fetch reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    if (!userProfile?.masterOwnerID) return;
    try {
      const sitesRef = collection(db, COLLECTIONS.SITES);
      const q = query(sitesRef, where('masterOwnerID', '==', userProfile.masterOwnerID));
      const snapshot = await getDocs(q);
      let siteList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Site[];
      if (userProfile.role === 'Clerk' && userProfile.assignedSites?.length) {
        siteList = siteList.filter((s) => userProfile.assignedSites!.includes(s.id));
      }
      setSites(siteList);
    } catch (err: any) {
      console.error('Error fetching sites:', err);
    }
  };

  useEffect(() => {
    if (userProfile) {
      fetchReports();
      fetchSites();
    }
  }, [userProfile]);

  const applyFilters = () => {
    let filtered = [...reports];

    if (statusFilter) {
      filtered = filtered.filter(report => report.status === statusFilter);
    }

    if (privacyFilter) {
      filtered = filtered.filter(report => report.privacy === privacyFilter);
    }

    if (siteFilter && !(isGovPlan && viewMode === 'map')) {
      filtered = filtered.filter(report => (report as any).readerDetails?.siteId === siteFilter);
    }

    if (reportTypeFilter === 'manual') {
      filtered = filtered.filter(report => report.isManualEntry === true);
    } else if (reportTypeFilter === 'device') {
      filtered = filtered.filter(report => report.isManualEntry !== true);
    }

    // Apply radius filter for public reports only
    if (radiusFilter !== 'all' && userLocation) {
      const radiusInMiles = parseInt(radiusFilter);
      filtered = filtered.filter(report => {
        // Only apply radius filter to public reports
        if (report.privacy !== 'public') {
          return true; // Keep all private reports regardless of distance
        }

        const coords = getReportCoordinates(report);
        if (coords.lat === null || coords.lng === null) {
          return false; // Exclude public reports without coordinates when radius filter is active
        }

        const distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          coords.lat,
          coords.lng
        );

        return distance <= radiusInMiles;
      });
    }
    // When radiusFilter is 'all', include all reports (public and private) regardless of coordinates

    setFilteredReports(filtered);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPrivacyFilter('');
    setRadiusFilter('all');
    setSiteFilter('');
    setReportTypeFilter('all');
    setFilterOpen(false);
  };

  const formatDate = (date: Date | Timestamp) => {
    const d = convertTimestamp(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Helper function to calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const getReportsWithCoordinates = () => {
    return filteredReports.filter(report => {
      const coords = getReportCoordinates(report);
      return coords.lat !== null && coords.lng !== null;
    });
  };

  const getReportCoordinates = (report: Report): { lat: number | null, lng: number | null } => {
    const anyReport = report as any;

    // Check various possible coordinate locations
    if (anyReport.location?.latitude && anyReport.location?.longitude) {
      return { lat: anyReport.location.latitude, lng: anyReport.location.longitude };
    }
    if (anyReport.location?.coordinates?.latitude && anyReport.location?.coordinates?.longitude) {
      return { lat: anyReport.location.coordinates.latitude, lng: anyReport.location.coordinates.longitude };
    }
    if (anyReport.readerDetails?.coordinates?.latitude && anyReport.readerDetails?.coordinates?.longitude) {
      return { lat: anyReport.readerDetails.coordinates.latitude, lng: anyReport.readerDetails.coordinates.longitude };
    }
    if (anyReport.coordinates?.latitude && anyReport.coordinates?.longitude) {
      return { lat: anyReport.coordinates.latitude, lng: anyReport.coordinates.longitude };
    }

    return { lat: null, lng: null };
  };

  /** Only treat explicit skimmer status as red; anything else (No Skimmer, Processing, undefined) = cyan. */
  const isSkimmerStatus = (status: string | undefined): boolean => {
    const s = String(status ?? '').trim();
    return s === 'Skimmer Detected!' || s === 'Skimmer Detected';
  };

  const getMarkerIcon = (status: string) => {
    const isSkimmer = isSkimmerStatus(status);
    return L.divIcon({
      className: 'map-leaflet-icon-wrap',
      html: `<div class="map-dot ${isSkimmer ? 'dot-red' : 'dot-cyan'}">${isSkimmer ? '<div class="pulse-ring"></div>' : ''}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    });
  };

  const getHeatMapData = () => {
    // Create heat map data points based on risk levels
    const heatPoints: [number, number, number][] = [];

    filteredReports.forEach(report => {
      const pt = approximateCoordinatesForPublicViewer(
        report,
        userProfile?.masterOwnerID,
        sites
      );
      if (pt == null || pt.lat == null || pt.lng == null) return;

      let intensity = 0.3;
      if (isSkimmerDetectedStatus(report.status)) {
        intensity = 1.0;
      } else if (isNoSkimmerStatus(report.status)) {
        intensity = 0.1;
      } else {
        intensity = 0.5;
      }

      heatPoints.push([pt.lat, pt.lng, intensity]);
    });

    return heatPoints;
  };

  // React component for heat layer
  const HeatLayer = () => {
    const map = useMap();

    React.useEffect(() => {
      console.log('HeatLayer useEffect triggered:', {
        hasMap: !!map,
        mapDisplayMode,
        filteredReportsCount: filteredReports.length
      });

      if (!map || mapDisplayMode !== 'heatmap') return;

      const heatData = getHeatMapData();
      console.log('Heat data generated:', heatData);

      if (heatData.length > 0) {
        try {
          // Check if heatLayer function exists
          if (!(L as any).heatLayer) {
            console.error('L.heatLayer is not available. Make sure leaflet.heat is properly loaded.');
            return;
          }

          const heatLayer = (L as any).heatLayer(heatData, {
            radius: 30,
            blur: 20,
            maxZoom: 17,
            minOpacity: 0.4,
            gradient: {
              0.0: '#00ff00', // Green for safe areas
              0.2: '#80ff00',
              0.4: '#ffff00', // Yellow for moderate risk
              0.6: '#ff8000',
              0.8: '#ff4000',
              1.0: '#ff0000'  // Red for high risk
            }
          });

          console.log('Adding heat layer to map:', heatLayer);
          heatLayer.addTo(map);

          return () => {
            console.log('Removing heat layer from map');
            if (map.hasLayer(heatLayer)) {
              map.removeLayer(heatLayer);
            }
          };
        } catch (error) {
          console.error('Error creating heat layer:', error);
        }
      } else {
        console.log('No heat data available to display');
      }
    }, [map, filteredReports, mapDisplayMode, sites, userProfile?.masterOwnerID]);

    return null;
  };

  const MapZoomControls = () => {
    const map = useMap();
    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          zIndex: 1001,
        }}
      >
        <Card sx={{ display: 'inline-flex', overflow: 'hidden', borderRadius: '50px', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
          <CardContent sx={{ py: 1.25, px: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Box display="flex" gap={0.5} sx={{ '& .MuiButton-root': { minWidth: 0 } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => map.zoomIn()}
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: muiTheme.palette.text.secondary,
                  borderRadius: '50px',
                  minWidth: 40,
                  px: 1.25,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: muiTheme.palette.text.primary,
                  },
                }}
              >
                <ZoomInIcon fontSize="small" />
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => map.zoomOut()}
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: muiTheme.palette.text.secondary,
                  borderRadius: '50px',
                  minWidth: 40,
                  px: 1.25,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: muiTheme.palette.text.primary,
                  },
                }}
              >
                <ZoomOutIcon fontSize="small" />
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  /**
   * Cross-org public reports: ~1 mi approximate area (not tied to viewer sites).
   * Renders under site markers so pins stay clickable on top.
   */
  const PublicCommunityApproxLayers: React.FC = () => {
    const viewerId = userProfile?.masterOwnerID;
    return (
      <>
        {filteredReports.map((report) => {
          if (!shouldRedactScannerIdentity(report, viewerId)) return null;
          const pt = approximateCoordinatesForPublicViewer(report, viewerId, sites);
          if (!pt) return null;
          return (
            <Circle
              key={`pub-approx-${report.id}`}
              center={[pt.lat, pt.lng]}
              radius={ONE_MILE_METERS}
              pathOptions={{ ...APPROX_AREA_CIRCLE_PATH }}
            >
              <Popup>
                <Box sx={{ minWidth: '220px', maxWidth: '300px' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {getReportSiteNameDisplay(report, viewerId)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    ~1 mile approximate area (public report)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getReportLocationAddressDisplay(report, viewerId)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }} fontWeight={isSkimmerStatus(report.status) ? 600 : 400} color={isSkimmerStatus(report.status) ? 'error.main' : 'text.secondary'}>
                    {report.status}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {getScannerDisplayLabel(report, viewerId)}
                  </Typography>
                </Box>
              </Popup>
            </Circle>
          );
        })}
      </>
    );
  };

  /** One marker per store (site) at store location regardless of zoom; red if any scan at that store found skimmer, else cyan. */
  const ReportsMapMarkers: React.FC = () => {
    const reportsWithCoords = getReportsWithCoordinates();
    const storesWithStatus = React.useMemo(() => {
      return sites
        .filter((s) => s.coordinates && typeof (s.coordinates as any).latitude === 'number' && typeof (s.coordinates as any).longitude === 'number')
        .map((site) => {
          const lat = (site.coordinates as any).latitude;
          const lng = (site.coordinates as any).longitude;
          const siteReports = reportsWithCoords
            .filter((r) => (r as any).readerDetails?.siteId === site.id)
            .sort((a, b) => convertTimestamp(b.createdAt).getTime() - convertTimestamp(a.createdAt).getTime());
          const hasSkimmer = siteReports.some((r) => isSkimmerStatus(r.status));
          return {
            siteId: site.id,
            siteName: site.name ?? 'Unknown Site',
            siteAddress: (site as any).address ?? '',
            lat,
            lng,
            reportCount: siteReports.length,
            hasSkimmer,
            reports: siteReports,
          };
        });
    }, [sites, reportsWithCoords]);

    return (
      <>
        {storesWithStatus.map((store) => (
          <Marker
            key={store.siteId}
            position={[store.lat, store.lng]}
            icon={getMarkerIcon(store.hasSkimmer ? 'Skimmer Detected!' : 'No Skimmer Detected')}
          >
            <Popup>
              <Box sx={{ minWidth: '260px', maxWidth: '320px' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {store.siteName}
                </Typography>
                {store.siteAddress && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {store.siteAddress}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {store.reportCount} scan{store.reportCount !== 1 ? 's' : ''}
                  {store.hasSkimmer && ` · Skimmer detected`}
                </Typography>
                {store.reports.length > 0 && (
                  <>
                    <Typography variant="caption" fontWeight="600" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
                      Scans
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1, px: 1, py: 0.5 }}>
                      {store.reports.map((r) => (
                        <Box key={r.id} sx={{ py: 0.75, borderBottom: '1px solid rgba(255,255,255,0.06)', '&:last-child': { borderBottom: 'none' } }}>
                          <Typography variant="body2" component="span">
                            {formatDate(r.createdAt)}
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ mx: 0.5 }}>·</Typography>
                          <Typography variant="body2" component="span" fontWeight={isSkimmerStatus(r.status) ? 600 : 400} color={isSkimmerStatus(r.status) ? 'error.main' : 'text.secondary'}>
                            {r.status}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {getScannerDisplayLabel(r, userProfile?.masterOwnerID)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </Popup>
          </Marker>
        ))}
      </>
    );
  };

  const handleEditReport = async (reportData: Partial<Report>) => {
    if (!editingReport) return;

    try {
      setUpdating(true);
      const reportRef = doc(db, COLLECTIONS.REPORTS, editingReport.id);

      const updateData = {
        ...reportData,
        updatedAt: Timestamp.now(),
      };

      // Remove undefined values
      const cleanedData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      ) as UpdateData<DocumentData>;

      await updateDoc(reportRef, cleanedData);

      // Update local state
      setReports(prev => prev.map(report =>
        report.id === editingReport.id
          ? { ...report, ...cleanedData }
          : report
      ));

      setEditDialogOpen(false);
      setEditingReport(null);
    } catch (error) {
      console.error('Error updating report:', error);
      setError('Failed to update report. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      setDeleting(true);
      const reportRef = doc(db, COLLECTIONS.REPORTS, reportToDelete.id);
      await deleteDoc(reportRef);

      // Update local state
      setReports(prev => prev.filter(report => report.id !== reportToDelete.id));

      setDeleteDialogOpen(false);
      setReportToDelete(null);
    } catch (error) {
      console.error('Error deleting report:', error);
      setError('Failed to delete report. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const openActionMenu = (event: React.MouseEvent<HTMLElement>, report: Report) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedReportForAction(report);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedReportForAction(null);
  };

  const openEditDialog = () => {
    if (selectedReportForAction) {
      setEditingReport(selectedReportForAction);
      setEditDialogOpen(true);
    }
    closeActionMenu();
  };

  const openDeleteDialog = () => {
    if (selectedReportForAction) {
      setReportToDelete(selectedReportForAction);
      setDeleteDialogOpen(true);
    }
    closeActionMenu();
  };

  const handleDownloadReport = async (report: Report) => {
    if (!canUserDownloadReport(report, userProfile?.masterOwnerID)) {
      setError('You can only download reports from your own company.');
      return;
    }
    try {
      setDownloadingReport(report.id);
      await generateEnhancedPDF(report as any, {
        includePhotos: true,  // Now enabled to show photos if available
        includeMap: true,     // Now enabled to show map if coordinates available
        reportType: 'incident',
        viewerMasterOwnerId: userProfile?.masterOwnerID,
      });
    } catch (error) {
      console.error('Failed to generate PDF report:', error);
      const msg = error instanceof Error ? error.message : '';
      setError(
        msg && (msg.includes('download') || msg.includes('signed in'))
          ? msg
          : 'Failed to generate PDF report. Please try again.'
      );
    } finally {
      setDownloadingReport(null);
    }
  };

  /** Folder browse: one entry per site name or per month; drill-down shows `items`. */
  const reportFolderGroups = useMemo(() => {
    if (listGroupMode === 'none') {
      return [] as { key: string; label: string; items: Report[] }[];
    }
    const viewer = userProfile?.masterOwnerID;
    const bucket = new Map<string, { label: string; items: Report[] }>();
    for (const r of filteredReports) {
      let key: string;
      let label: string;
      if (listGroupMode === 'site') {
        label = getReportSiteFolderLabel(r, viewer);
        key = label.toLowerCase();
      } else {
        const d = convertTimestamp(r.createdAt as any);
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      }
      if (!bucket.has(key)) {
        bucket.set(key, { label, items: [] });
      }
      bucket.get(key)!.items.push(r);
    }
    const rows = Array.from(bucket.entries()).map(([k, v]) => ({
      key: k,
      label: v.label,
      items: v.items,
    }));
    rows.sort((a, b) =>
      listGroupMode === 'month' ? b.key.localeCompare(a.key) : a.label.localeCompare(b.label)
    );
    return rows;
  }, [filteredReports, listGroupMode, userProfile?.masterOwnerID]);

  const currentDrillGroup = folderDrillKey
    ? reportFolderGroups.find((g) => g.key === folderDrillKey)
    : undefined;

  useEffect(() => {
    setFolderDrillKey(null);
  }, [listGroupMode]);

  useEffect(() => {
    if (folderDrillKey != null && !reportFolderGroups.some((g) => g.key === folderDrillKey)) {
      setFolderDrillKey(null);
    }
  }, [folderDrillKey, reportFolderGroups]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={200}>
        <CircularProgress size={40} sx={{ color: colors.Gradient }} />
      </Box>
    );
  }

  return (
    <>
      {/* Analytics Section with Tabs — card-4 */}
      <Card
        className="card-4"
        sx={{
          marginBottom: 3,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          '&:hover': {
            border: '1px solid rgba(34, 211, 238, 0.4)',
            boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <CardContent sx={{ py: 3, px: 3, '&:last-child': { pb: 3 } }}>
        {/* Tab Headers — Reports Overview label (section overline) + toggles in same row, divider below */}
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(34, 211, 238, 0.2)', paddingBottom: 2, marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
            }}
          >
            Reports Overview
          </Typography>
          <Card
            sx={{
              display: 'inline-flex',
              overflow: 'hidden',
              borderRadius: '50px',
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderTop: '1px solid rgba(255, 255, 255, 0.15)',
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(34, 211, 238, 0.3)',
                boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
              },
            }}
          >
            <CardContent sx={{ py: 1.25, px: 1.25, '&:last-child': { pb: 1.25 } }}>
              <Box display="flex" gap={0.5} sx={{ '& .MuiButton-root': { minWidth: 0 } }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAnalyticsTab('overview')}
                  sx={{
                    backgroundColor: analyticsTab === 'overview' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: analyticsTab === 'overview' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: analyticsTab === 'overview' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: analyticsTab === 'overview' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: analyticsTab === 'overview' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: analyticsTab === 'overview' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  Overview
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAnalyticsTab('patterns')}
                  sx={{
                    backgroundColor: analyticsTab === 'patterns' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: analyticsTab === 'patterns' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: analyticsTab === 'patterns' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: analyticsTab === 'patterns' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: analyticsTab === 'patterns' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: analyticsTab === 'patterns' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  Analytics
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAnalyticsTab('hotzones')}
                  sx={{
                    backgroundColor: analyticsTab === 'hotzones' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: analyticsTab === 'hotzones' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: analyticsTab === 'hotzones' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: analyticsTab === 'hotzones' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: analyticsTab === 'hotzones' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: analyticsTab === 'hotzones' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  High Risk Areas
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Tab Content */}
        {analyticsTab === 'overview' && (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
              {/* Total Reports — Interactive High-Blur Glass */}
              <Box
                sx={{
                  textAlign: 'center',
                  padding: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                    transform: 'translateY(-5px)',
                    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                  },
                  '&:hover::before': { left: '100%' },
                }}
              >
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  {filteredReports.length}
                </Typography>
                <Typography variant="body1" color="text.secondary" fontWeight="500">
                  Total Reports
                </Typography>
              </Box>

              {/* Skimmers Detected — Interactive High-Blur Glass */}
              <Box
                sx={{
                  textAlign: 'center',
                  padding: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                    transform: 'translateY(-5px)',
                    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                  },
                  '&:hover::before': { left: '100%' },
                }}
              >
                <Typography variant="h3" color="error.main" fontWeight="bold">
                  {filteredReports.filter(report => isSkimmerDetectedStatus(report.status)).length}
                </Typography>
                <Typography variant="body1" color="text.secondary" fontWeight="500">
                  Skimmers Detected
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {filteredReports.length > 0
                    ? `(${Math.round((filteredReports.filter(report => isSkimmerDetectedStatus(report.status)).length / filteredReports.length) * 100)}%)`
                    : '(0%)'
                  }
                </Typography>
              </Box>

              {/* No Skimmers Detected — Interactive High-Blur Glass */}
              <Box
                sx={{
                  textAlign: 'center',
                  padding: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                    transform: 'translateY(-5px)',
                    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                  },
                  '&:hover::before': { left: '100%' },
                }}
              >
                <Typography variant="h3" color="success.main" fontWeight="bold">
                  {filteredReports.filter(report => report.status === 'No Skimmer Detected').length}
                </Typography>
                <Typography variant="body1" color="text.secondary" fontWeight="500">
                  No Skimmers Detected
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {filteredReports.length > 0
                    ? `(${Math.round((filteredReports.filter(report => report.status === 'No Skimmer Detected').length / filteredReports.length) * 100)}%)`
                    : '(0%)'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {analyticsTab === 'patterns' && (
          <Box>
            <Typography
              component="span"
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                color: '#22d3ee',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                display: 'block',
                marginBottom: 2,
              }}
            >
              Pattern Analytics
            </Typography>

            {(() => {
              // Common Threat Vectors - Device types most targeted
              const deviceThreatStats = filteredReports.reduce((acc: any, report) => {
                if (isSkimmerDetectedStatus(report.status)) {
                  const deviceType = report.readerDetails?.device?.deviceType || 'Unknown Device';
                  const modelName = report.readerDetails?.device?.modelName || 'Unknown Model';
                  const key = `${deviceType} - ${modelName}`;

                  if (!acc[key]) {
                    acc[key] = {
                      deviceType,
                      modelName,
                      threatCount: 0,
                      totalScans: 0,
                      riskScore: 0
                    };
                  }
                  acc[key].threatCount++;
                }

                // Count total scans for each device type
                const deviceType = report.readerDetails?.device?.deviceType || 'Unknown Device';
                const modelName = report.readerDetails?.device?.modelName || 'Unknown Model';
                const key = `${deviceType} - ${modelName}`;

                if (!acc[key]) {
                  acc[key] = {
                    deviceType,
                    modelName,
                    threatCount: 0,
                    totalScans: 0,
                    riskScore: 0
                  };
                }
                acc[key].totalScans++;
                acc[key].riskScore = acc[key].totalScans > 0 ? Math.round((acc[key].threatCount / acc[key].totalScans) * 100) : 0;

                return acc;
              }, {});

              const topThreatVectors = Object.values(deviceThreatStats)
                .filter((device: any) => device.threatCount > 0)
                .sort((a: any, b: any) => b.threatCount - a.threatCount)
                .slice(0, 3);

              // Geographic Clustering - Areas with concentrated threats  
              const geographicClusters = filteredReports.reduce((acc: any, report) => {
                if (isSkimmerDetectedStatus(report.status)) {
                  const address = getReportLocationAddressDisplay(report, userProfile?.masterOwnerID);
                  const area = address.split(',')[1]?.trim() || address.split(' ').slice(-2).join(' ') || 'Unknown Area';

                  if (!acc[area]) {
                    acc[area] = { area, threatCount: 0, coordinates: [] };
                  }
                  acc[area].threatCount++;

                  const pt = approximateCoordinatesForPublicViewer(
                    report,
                    userProfile?.masterOwnerID,
                    sites
                  );
                  if (pt?.lat != null && pt?.lng != null) {
                    acc[area].coordinates.push([pt.lat, pt.lng]);
                  }
                }
                return acc;
              }, {});

              const topClusters = Object.values(geographicClusters)
                .filter((cluster: any) => cluster.threatCount > 0)
                .sort((a: any, b: any) => b.threatCount - a.threatCount)
                .slice(0, 3);

              // Temporal Patterns - Time/day when threats occur most
              const temporalPatterns = filteredReports.reduce((acc: any, report) => {
                if (isSkimmerDetectedStatus(report.status)) {
                  const date = convertTimestamp(report.createdAt);
                  const hour = date.getHours();
                  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

                  // Time of day patterns
                  const timeSlot = hour < 6 ? 'Night (12-6 AM)' :
                    hour < 12 ? 'Morning (6-12 PM)' :
                      hour < 18 ? 'Afternoon (12-6 PM)' : 'Evening (6-12 PM)';

                  if (!acc.timeSlots[timeSlot]) acc.timeSlots[timeSlot] = 0;
                  acc.timeSlots[timeSlot]++;

                  // Day of week patterns
                  if (!acc.dayOfWeek[dayOfWeek]) acc.dayOfWeek[dayOfWeek] = 0;
                  acc.dayOfWeek[dayOfWeek]++;
                }
                return acc;
              }, { timeSlots: {}, dayOfWeek: {} });

              const topTimeSlot = Object.entries(temporalPatterns.timeSlots)
                .sort(([, a]: any, [, b]: any) => b - a)[0];
              const topDayOfWeek = Object.entries(temporalPatterns.dayOfWeek)
                .sort(([, a]: any, [, b]: any) => b - a)[0];

              // Device Vulnerability Index
              const deviceVulnerabilityIndex = Object.values(deviceThreatStats)
                .filter((device: any) => device.totalScans >= 2) // Only devices with multiple scans
                .sort((a: any, b: any) => b.riskScore - a.riskScore)
                .slice(0, 3);

              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
                  {/* Common Threat Vectors — Interactive High-Blur Glass (error/red tint) */}
                  <Box
                    sx={{
                      padding: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      background: 'rgba(255, 255, 255, 0.03)',
                      backgroundImage: 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, transparent 100%)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderTop: '1px solid rgba(244, 67, 54, 0.25)',
                      borderLeft: '1px solid rgba(244, 67, 54, 0.15)',
                      borderRadius: '20px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                        transition: 'left 0.5s',
                      },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(244, 67, 54, 0.35)',
                        boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(244, 67, 54, 0.08)',
                      },
                      '&:hover::before': { left: '100%' },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color="error.main" gutterBottom>
                      Common Threat Vectors
                    </Typography>
                    {topThreatVectors.length > 0 ? (
                      topThreatVectors.map((device: any, index: number) => (
                        <Box key={index} sx={{ marginBottom: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" color="text.primary" fontWeight="500">
                              {device.deviceType}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {device.modelName}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${device.threatCount} threats`}
                            size="small"
                            sx={{
                              backgroundColor: muiTheme.palette.error.main,
                              color: muiTheme.palette.error.contrastText,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">No threat patterns detected</Typography>
                    )}
                  </Box>

                  {/* Geographic Clustering — Interactive High-Blur Glass (warning/amber tint) */}
                  <Box
                    sx={{
                      padding: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      background: 'rgba(255, 255, 255, 0.03)',
                      backgroundImage: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, transparent 100%)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderTop: '1px solid rgba(255, 152, 0, 0.25)',
                      borderLeft: '1px solid rgba(255, 152, 0, 0.15)',
                      borderRadius: '20px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                        transition: 'left 0.5s',
                      },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(255, 152, 0, 0.35)',
                        boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(255, 152, 0, 0.08)',
                      },
                      '&:hover::before': { left: '100%' },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color="warning.main" gutterBottom>
                      Geographic Clustering
                    </Typography>
                    {topClusters.length > 0 ? (
                      topClusters.map((cluster: any, index: number) => (
                        <Box key={index} sx={{ marginBottom: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.primary" fontWeight="500">
                            {cluster.area}
                          </Typography>
                          <Chip
                            label={`${cluster.threatCount} threats`}
                            size="small"
                            sx={{
                              backgroundColor: muiTheme.palette.warning.main,
                              color: muiTheme.palette.warning.contrastText,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">No geographic clusters found</Typography>
                    )}
                  </Box>

                  {/* Temporal Patterns — Interactive High-Blur Glass (info/blue tint) */}
                  <Box
                    sx={{
                      padding: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      background: 'rgba(255, 255, 255, 0.03)',
                      backgroundImage: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, transparent 100%)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderTop: '1px solid rgba(33, 150, 243, 0.25)',
                      borderLeft: '1px solid rgba(33, 150, 243, 0.15)',
                      borderRadius: '20px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                        transition: 'left 0.5s',
                      },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(33, 150, 243, 0.35)',
                        boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(33, 150, 243, 0.08)',
                      },
                      '&:hover::before': { left: '100%' },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color="info.main" gutterBottom>
                      Temporal Patterns
                    </Typography>
                    {topTimeSlot || topDayOfWeek ? (
                      <Box>
                        {topTimeSlot && (
                          <Box sx={{ marginBottom: 1 }}>
                            <Typography variant="body2" color="text.primary" fontWeight="500">
                              Peak Time: {topTimeSlot[0]}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {`${topTimeSlot[1]} threats detected`}
                            </Typography>
                          </Box>
                        )}
                        {topDayOfWeek && (
                          <Box>
                            <Typography variant="body2" color="text.primary" fontWeight="500">
                              Peak Day: {topDayOfWeek[0]}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {`${topDayOfWeek[1]} threats detected`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">No temporal patterns detected</Typography>
                    )}
                  </Box>

                  {/* Device Vulnerability Index — Interactive High-Blur Glass (primary/cyan tint) */}
                  <Box
                    sx={{
                      padding: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      background: 'rgba(255, 255, 255, 0.03)',
                      backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.08) 0%, transparent 100%)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderTop: '1px solid rgba(34, 211, 238, 0.25)',
                      borderLeft: '1px solid rgba(34, 211, 238, 0.15)',
                      borderRadius: '20px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                        transition: 'left 0.5s',
                      },
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: '1px solid rgba(34, 211, 238, 0.35)',
                        boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.08)',
                      },
                      '&:hover::before': { left: '100%' },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                      Device Vulnerability Index
                    </Typography>
                    {deviceVulnerabilityIndex.length > 0 ? (
                      deviceVulnerabilityIndex.map((device: any, index: number) => (
                        <Box key={index} sx={{ marginBottom: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" color="text.primary" fontWeight="500">
                              {device.deviceType}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {device.totalScans} scans total
                            </Typography>
                          </Box>
                          <Chip
                            label={`${device.riskScore}% risk`}
                            size="small"
                            sx={{
                              backgroundColor: device.riskScore > 50 ? muiTheme.palette.error.main : device.riskScore > 20 ? muiTheme.palette.warning.main : muiTheme.palette.success.main,
                              color: device.riskScore > 50 ? muiTheme.palette.error.contrastText : device.riskScore > 20 ? muiTheme.palette.warning.contrastText : muiTheme.palette.success.contrastText,
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">Insufficient data for risk assessment</Typography>
                    )}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}

        {analyticsTab === 'hotzones' && (
          <Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Hot Zones - High Risk Locations
            </Typography>

            {(() => {
              const viewerId = userProfile?.masterOwnerID;
              const hotZoneReports = filteredReports.filter(
                (report) => report.privacy !== 'public' || isSkimmerDetectedStatus(report.status)
              );

              const locationStats = hotZoneReports.reduce((acc: any, report) => {
                const redact = shouldRedactScannerIdentity(report, viewerId);
                let key: string;
                if (redact) {
                  const approx = approximateCoordinatesForPublicViewer(report, viewerId, sites);
                  key = approx
                    ? `r|${approx.lat.toFixed(2)}|${approx.lng.toFixed(2)}`
                    : `r|${getPublicApproximateLocationLabel(report)}`;
                } else {
                  const siteName = (report as any).readerDetails?.siteName || 'Unknown Location';
                  const address = (report as any).location?.address || '';
                  key = `${siteName}|${address}`;
                }

                if (!acc[key]) {
                  acc[key] = {
                    siteName: (report as any).readerDetails?.siteName || 'Unknown Location',
                    address: (report as any).location?.address || '',
                    totalScans: 0,
                    skimmersFound: 0,
                    safeScans: 0,
                    riskPercentage: 0,
                    redactZone: redact,
                    approxLabel: redact ? getPublicApproximateLocationLabel(report) : '',
                  };
                }

                if (redact) {
                  acc[key].redactZone = true;
                  if (!acc[key].approxLabel) {
                    acc[key].approxLabel = getPublicApproximateLocationLabel(report);
                  }
                }

                acc[key].totalScans++;
                if (isSkimmerDetectedStatus(report.status)) {
                  acc[key].skimmersFound++;
                } else if (isNoSkimmerStatus(report.status)) {
                  acc[key].safeScans++;
                }

                acc[key].riskPercentage = Math.round((acc[key].skimmersFound / acc[key].totalScans) * 100);

                return acc;
              }, {});

              const hotZones = Object.values(locationStats)
                .filter((zone: any) => zone.skimmersFound > 0)
                .sort((a: any, b: any) => {
                  if (b.skimmersFound !== a.skimmersFound) {
                    return b.skimmersFound - a.skimmersFound;
                  }
                  return b.riskPercentage - a.riskPercentage;
                })
                .slice(0, 5);

              return (
                <Box>
                  {hotZones.length > 0 ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
                      {hotZones.map((zone: any, index: number) => (
                        <Box
                          key={index}
                          sx={{
                            padding: 2,
                            position: 'relative',
                            overflow: 'hidden',
                            background: 'rgba(255, 255, 255, 0.03)',
                            backgroundImage: zone.skimmersFound > 0
                              ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, transparent 100%)'
                              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: zone.skimmersFound > 0 ? '1px solid rgba(244, 67, 54, 0.35)' : '1px solid rgba(255, 255, 255, 0.08)',
                            borderTop: zone.skimmersFound > 0 ? '1px solid rgba(244, 67, 54, 0.4)' : '1px solid rgba(255, 255, 255, 0.15)',
                            borderLeft: zone.skimmersFound > 0 ? '1px solid rgba(244, 67, 54, 0.25)' : '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '20px',
                            boxShadow: zone.skimmersFound > 0
                              ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(244, 67, 54, 0.06)'
                              : '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                              transition: 'left 0.5s',
                            },
                            '&:hover': {
                              background: zone.skimmersFound > 0 ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                              border: zone.skimmersFound > 0 ? '1px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(255, 255, 255, 0.15)',
                              boxShadow: zone.skimmersFound > 0
                                ? '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(244, 67, 54, 0.1)'
                                : '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(255, 255, 255, 0.05)',
                            },
                            '&:hover::before': { left: '100%' },
                          }}
                        >
                          {/* Risk Badge */}
                          {zone.skimmersFound > 0 && (
                            <Chip
                              label="HIGH RISK"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: muiTheme.palette.error.main,
                                color: muiTheme.palette.error.contrastText,
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                              }}
                            />
                          )}

                          {/* Location Info — no street/site PII for other orgs' public reports */}
                          <Typography variant="subtitle1" fontWeight="bold" color="text.primary" gutterBottom>
                            {zone.redactZone ? zone.approxLabel || 'Approximate area' : zone.siteName}
                          </Typography>

                          {!zone.redactZone && zone.address && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {zone.address}
                            </Typography>
                          )}
                          {zone.redactZone && (
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Community report · approximate location only
                            </Typography>
                          )}

                          {/* Stats */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, marginTop: 2 }}>
                            <Box textAlign="center">
                              <Typography variant="h6" color="primary.main" fontWeight="bold">
                                {zone.totalScans}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Total Scans
                              </Typography>
                            </Box>

                            <Box textAlign="center">
                              <Typography variant="h6" color="error.main" fontWeight="bold">
                                {zone.skimmersFound}
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Threats Found
                              </Typography>
                            </Box>

                            <Box textAlign="center">
                              <Typography
                                variant="h6"
                                color={zone.riskPercentage > 50 ? "error.main" : zone.riskPercentage > 20 ? "warning.main" : "success.main"}
                                fontWeight="bold"
                              >
                                {zone.riskPercentage}%
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Risk Level
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box textAlign="center" padding={3}>
                      <Typography color="text.secondary">
                        No high-risk locations detected. All scanned areas appear safe!
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>
        )}
        </CardContent>
      </Card>

      <Card className="card-4" sx={{ marginBottom: 3 }}>
        <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography
              component="span"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 700,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
              }}
            >
              Scan Reports
            </Typography>

            <Box display="flex" gap={2} alignItems="center">
              <Card
                sx={{
                  display: 'inline-flex',
                  overflow: 'hidden',
                  borderRadius: '50px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(34, 211, 238, 0.3)',
                    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                  },
                }}
              >
                <CardContent sx={{ py: 1.25, px: 1.25, '&:last-child': { pb: 1.25 } }}>
                  <Box display="flex" gap={1.25} sx={{ '& .MuiButton-root': { minWidth: 0 } }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setViewMode('list')}
                  sx={{
                    backgroundColor: viewMode === 'list' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: viewMode === 'list' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: viewMode === 'list' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: viewMode === 'list' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    minWidth: 44,
                    px: 1.75,
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: viewMode === 'list' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: viewMode === 'list' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  <ListIcon fontSize="small" />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setViewMode('map')}
                  sx={{
                    backgroundColor: viewMode === 'map' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                    borderColor: viewMode === 'map' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                    color: viewMode === 'map' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                    boxShadow: viewMode === 'map' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                    borderRadius: '50px',
                    minWidth: 44,
                    px: 1.75,
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: viewMode === 'map' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: viewMode === 'map' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                      color: muiTheme.palette.text.primary,
                    },
                  }}
                >
                  <MapIcon fontSize="small" />
                </Button>
                  </Box>
                </CardContent>
              </Card>

              {viewMode === 'list' && (
                <FormControl size="small" sx={{ minWidth: 168 }}>
                  <InputLabel id="folder-view-label">Folder view</InputLabel>
                  <Select
                    labelId="folder-view-label"
                    label="Folder view"
                    value={listGroupMode}
                    onChange={(e) => setListGroupMode(e.target.value as 'none' | 'site' | 'month')}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="none">Flat list</MenuItem>
                    <MenuItem value="site">By site name</MenuItem>
                    <MenuItem value="month">By month (sweeps)</MenuItem>
                  </Select>
                </FormControl>
              )}

              <Button
                startIcon={<FilterIcon fontSize="small" />}
            variant="outlined"
            size="small"
            onClick={() => setFilterOpen(true)}
            sx={{
              backgroundColor: 'transparent',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              color: muiTheme.palette.text.secondary,
              borderRadius: '50px',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '0.8125rem',
              py: 0.5,
              px: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: muiTheme.palette.text.primary,
              },
            }}
          >
            Filter
          </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {filteredReports.length === 0 ? (
          <Box textAlign="center" padding={4}>
            <Typography color={colors.TextSecondary} variant="h6" gutterBottom>
              No reports found
            </Typography>
            <Typography color={colors.TextMuted}>
              {reports.length === 0
                ? 'No scan reports have been generated yet.'
                : 'Try adjusting your filters to see more reports.'
              }
            </Typography>
          </Box>
        ) : viewMode === 'map' ? (
          <Box sx={{ paddingTop: 3 }}>
            <Card
              sx={{
                position: 'relative',
                overflow: 'hidden',
                marginBottom: 2,
                background: 'rgba(255, 255, 255, 0.03)',
                backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
                  transition: 'left 0.5s',
                },
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(34, 211, 238, 0.3)',
                  boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                },
                '&:hover::before': { left: '100%' },
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "'JetBrains Mono', monospace",
                      color: '#22d3ee',
                      fontSize: '13px',
                      fontWeight: 500,
                      letterSpacing: '0.3em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Reports Map
                  </Typography>

                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Typography variant="body2" color={colors.TextSecondary}>
                      {getReportsWithCoordinates().length} of {filteredReports.length} reports have location data
                    </Typography>
                    <Card
                      sx={{
                        display: 'inline-flex',
                        overflow: 'hidden',
                        borderRadius: '50px',
                        marginLeft: 'auto',
                        background: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(34, 211, 238, 0.3)',
                          boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.7), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                        },
                      }}
                    >
                      <CardContent sx={{ py: 1.25, px: 1.25, '&:last-child': { pb: 1.25 } }}>
                        <Box display="flex" gap={0.5} sx={{ '& .MuiButton-root': { minWidth: 0 } }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setMapDisplayMode('markers')}
                            sx={{
                              backgroundColor: mapDisplayMode === 'markers' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                              borderColor: mapDisplayMode === 'markers' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                              color: mapDisplayMode === 'markers' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                              boxShadow: mapDisplayMode === 'markers' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                              borderRadius: '50px',
                              fontFamily: "'Inter', sans-serif",
                              fontWeight: 'bold',
                              textTransform: 'none',
                              fontSize: '0.8125rem',
                              py: 0.5,
                              px: 1.5,
                              '&:hover': {
                                backgroundColor: mapDisplayMode === 'markers' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                                borderColor: mapDisplayMode === 'markers' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                                color: muiTheme.palette.text.primary,
                              },
                            }}
                          >
                            Markers
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setMapDisplayMode('heatmap')}
                            sx={{
                              backgroundColor: mapDisplayMode === 'heatmap' ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                              borderColor: mapDisplayMode === 'heatmap' ? 'rgba(34, 211, 238, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                              color: mapDisplayMode === 'heatmap' ? muiTheme.palette.text.primary : muiTheme.palette.text.secondary,
                              boxShadow: mapDisplayMode === 'heatmap' ? 'inset 0 0 20px rgba(34, 211, 238, 0.1)' : 'none',
                              borderRadius: '50px',
                              fontFamily: "'Inter', sans-serif",
                              fontWeight: 'bold',
                              textTransform: 'none',
                              fontSize: '0.8125rem',
                              py: 0.5,
                              px: 1.5,
                              '&:hover': {
                                backgroundColor: mapDisplayMode === 'heatmap' ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                                borderColor: mapDisplayMode === 'heatmap' ? '#22d3ee' : 'rgba(255, 255, 255, 0.2)',
                                color: muiTheme.palette.text.primary,
                              },
                            }}
                          >
                            Heat Map
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>

                <Box sx={{ height: '600px', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
                  {getReportsWithCoordinates().length > 0 ? (
                    <>
                      <MapContainer
                        center={[25.7617, -80.1918]}
                        zoom={3}
                        zoomControl={false}
                        attributionControl={false}
                        style={{ height: '100%', width: '100%', background: '#0a0a0f' }}
                      >
                        <TileLayer url={DARK_TILES} />
                        <MapZoomControls />

                        {/* ~1 mi area for other orgs' public reports (approximate coords) */}
                        <PublicCommunityApproxLayers />

                        {/* Heat Map Layer */}
                        {mapDisplayMode === 'heatmap' && <HeatLayer />}

                        {/* One marker per store at store location; red if any scan at that store found skimmer, else cyan. */}
                        {mapDisplayMode === 'markers' && <ReportsMapMarkers />}
                      </MapContainer>
                      <div className="map-overlay" aria-hidden="true">
                        <div className="map-overlay-teal" />
                        <div className="map-overlay-grid" />
                      </div>
                    </>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                      sx={{ backgroundColor: colors.Background, borderRadius: '10px' }}
                    >
                      <Typography color={colors.TextSecondary}>
                        No reports with location data to display on map
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2, paddingTop: 3, maxWidth: 1380, mx: 'auto' }}>
                {listGroupMode === 'none' &&
                  filteredReports.map((report) => (
                    <ReportListRowCard
                      key={report.id}
                      report={report}
                      userProfile={userProfile}
                      colors={colors}
                      downloadingReport={downloadingReport}
                      formatDate={formatDate}
                      onOpenDetails={(r) => {
                        setSelectedReport(r);
                        setDetailsDialogOpen(true);
                      }}
                      onEdit={(r, e) => {
                        e.stopPropagation();
                        setSelectedReportForAction(r);
                        setEditingReport(r);
                        setEditDialogOpen(true);
                      }}
                      onDelete={(r, e) => {
                        e.stopPropagation();
                        setSelectedReportForAction(r);
                        setReportToDelete(r);
                        setDeleteDialogOpen(true);
                      }}
                      onDownload={(r, e) => {
                        e.stopPropagation();
                        handleDownloadReport(r);
                      }}
                    />
                  ))}

                {listGroupMode !== 'none' && folderDrillKey === null && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 2,
                    }}
                  >
                    {reportFolderGroups.length === 0 ? (
                      <Typography color={colors.TextSecondary}>No reports in this view.</Typography>
                    ) : (
                      reportFolderGroups.map((g) => (
                        <Card
                          key={g.key}
                          onClick={() => setFolderDrillKey(g.key)}
                          sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            background: 'rgba(255, 255, 255, 0.03)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(34, 211, 238, 0.25)',
                            borderRadius: '20px',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: 'rgba(34, 211, 238, 0.5)',
                              transform: 'translateY(-4px)',
                            },
                          }}
                        >
                          <CardContent sx={{ py: 3 }}>
                            <Box display="flex" alignItems="flex-start" gap={1.5} mb={1}>
                              <FolderOpenIcon sx={{ color: '#22d3ee', fontSize: 28, mt: 0.25 }} />
                              <Box flex={1} minWidth={0}>
                                <Typography variant="h6" color={colors.TextPrimary} sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                  {g.label}
                                </Typography>
                                <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 0.5 }}>
                                  {listGroupMode === 'site' ? 'Site name' : 'Reporting month'}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              size="small"
                              label={`${g.items.length} report${g.items.length === 1 ? '' : 's'}`}
                              sx={{ fontWeight: 600, borderRadius: '50px' }}
                            />
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Box>
                )}

                {listGroupMode !== 'none' && folderDrillKey !== null && currentDrillGroup && (
                  <>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Button
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                        size="small"
                        onClick={() => setFolderDrillKey(null)}
                        sx={{
                          borderRadius: '50px',
                          textTransform: 'none',
                          color: '#fff',
                          borderColor: 'rgba(255,255,255,0.2)',
                        }}
                      >
                        All folders
                      </Button>
                      <Typography variant="subtitle1" color={colors.TextPrimary} sx={{ fontWeight: 700 }}>
                        {currentDrillGroup.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${currentDrillGroup.items.length} report${currentDrillGroup.items.length === 1 ? '' : 's'}`}
                        sx={{ borderRadius: '50px' }}
                      />
                    </Box>
                    {currentDrillGroup.items.map((report) => (
                      <ReportListRowCard
                        key={report.id}
                        report={report}
                        userProfile={userProfile}
                        colors={colors}
                        downloadingReport={downloadingReport}
                        formatDate={formatDate}
                        onOpenDetails={(r) => {
                          setSelectedReport(r);
                          setDetailsDialogOpen(true);
                        }}
                        onEdit={(r, e) => {
                          e.stopPropagation();
                          setSelectedReportForAction(r);
                          setEditingReport(r);
                          setEditDialogOpen(true);
                        }}
                        onDelete={(r, e) => {
                          e.stopPropagation();
                          setSelectedReportForAction(r);
                          setReportToDelete(r);
                          setDeleteDialogOpen(true);
                        }}
                        onDownload={(r, e) => {
                          e.stopPropagation();
                          handleDownloadReport(r);
                        }}
                      />
                    ))}
                  </>
                )}
              </Box>
        )}
        </CardContent>
      </Card>

      {/* Filter Dialog */}
      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          Filter Reports
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          <Box sx={{ marginTop: 1 }}>
            <TextField
              fullWidth
              label="Status"
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ marginBottom: 2 }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="No Skimmer Detected">No Skimmer Detected</MenuItem>
              <MenuItem value="Skimmer Detected!">Skimmer Detected</MenuItem>
              <MenuItem value="Proccessing Data">Processing Data</MenuItem>
            </TextField>

            {!(isGovPlan && viewMode === 'map') && (
              <TextField
                fullWidth
                label="Site"
                select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                sx={{ marginBottom: 2 }}
              >
                <MenuItem value="">All Sites</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>{site.name}</MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              label="Report source"
              select
              value={reportTypeFilter}
              onChange={(e) => setReportTypeFilter(e.target.value as 'all' | 'manual' | 'device')}
              sx={{ marginBottom: 2 }}
            >
              <MenuItem value="all">All (manual + device)</MenuItem>
              <MenuItem value="manual">Manual entry</MenuItem>
              <MenuItem value="device">Device scan</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Privacy"
              select
              value={privacyFilter}
              onChange={(e) => setPrivacyFilter(e.target.value)}
              sx={{ marginBottom: 2 }}
            >
              <MenuItem value="">All Privacy Levels</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Radius (Public Reports Only)"
              select
              value={radiusFilter}
              onChange={(e) => setRadiusFilter(e.target.value)}
              helperText={
                geocodingError
                  ? geocodingError
                  : companyAddress
                    ? `Distance from company: ${companyAddress}`
                    : "Loading company address..."
              }
            >
              <MenuItem value="all">All Distances</MenuItem>
              <MenuItem value="1">1 mile</MenuItem>
              <MenuItem value="5">5 miles</MenuItem>
              <MenuItem value="10">10 miles</MenuItem>
              <MenuItem value="15">15 miles</MenuItem>
              <MenuItem value="20">20 miles</MenuItem>
              <MenuItem value="25">25 miles</MenuItem>
              <MenuItem value="30">30 miles</MenuItem>
              <MenuItem value="50">50 miles</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={clearFilters}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Clear Filters
          </Button>
          <Button
            onClick={() => setFilterOpen(false)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Details Dialog */}
      <ReportDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedReport(null);
        }}
        report={selectedReport as any}
        sites={sites}
      />

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        PaperProps={{
          sx: {
            backgroundColor: colors.CardBackground,
            border: `1px solid ${colors.TextMuted}`,
          },
        }}
      >
        <MenuItem onClick={openEditDialog}>
          <ListItemIcon>
            <EditIcon sx={{ color: colors.Gradient }} />
          </ListItemIcon>
          <ListItemText primary="Edit Report" />
        </MenuItem>
        <MenuItem onClick={openDeleteDialog}>
          <ListItemIcon>
            <DeleteIcon sx={{ color: colors.Error }} />
          </ListItemIcon>
          <ListItemText primary="Delete Report" />
        </MenuItem>
      </Menu>

      {/* Edit Report Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          Edit Report
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          {editingReport && (
            <Box sx={{ marginTop: 1 }}>
              <TextField
                fullWidth
                label="Status"
                select
                value={editingReport.status}
                onChange={(e) => setEditingReport({
                  ...editingReport,
                  status: e.target.value as Report['status']
                })}
                sx={{ marginBottom: 2 }}
              >
                <MenuItem value="No Skimmer Detected">No Skimmer Detected</MenuItem>
                <MenuItem value="Skimmer Detected!">Skimmer Detected!</MenuItem>
                <MenuItem value="Proccessing Data">Processing Data</MenuItem>
              </TextField>

              <TextField
                fullWidth
                label="Privacy"
                select
                value={editingReport.privacy}
                onChange={(e) => setEditingReport({
                  ...editingReport,
                  privacy: e.target.value as 'public' | 'private'
                })}
                sx={{ marginBottom: 2 }}
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </TextField>

              <TextField
                fullWidth
                label="Message/Notes"
                multiline
                rows={3}
                value={editingReport.message || ''}
                onChange={(e) => setEditingReport({
                  ...editingReport,
                  message: e.target.value
                })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleEditReport(editingReport!)}
            variant="outlined"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : undefined}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
              '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: colors.TextMuted, background: 'rgba(255,255,255,0.05)' },
            }}
          >
            {updating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportsTab;
